import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import CurrencyDisplay from './CurrencyDisplay';
import { 
  Trash2, 
  AlertTriangle, 
  CheckSquare, 
  Square,
  X,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const DataManager = ({ 
  collectionName,        // اسم المجموعة في قاعدة البيانات
  collectionLabel,       // اسم الصفحة بالعربي
  items = [],            // البيانات الحالية
  onRefresh,             // دالة لتحديث البيانات
  idField = 'id',        // اسم حقل المعرف
  nameField = 'name',    // اسم حقل الاسم للعرض
  isAdmin = false        // هل المستخدم مدير
}) => {
  const [showManager, setShowManager] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState('selected'); // 'selected' or 'all'
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isAdmin) return null;

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const selectAll = () => {
    if (selectedIds.length === items.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map(item => item[idField]));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) {
      toast.error('لم يتم تحديد أي عنصر');
      return;
    }
    setDeleteType('selected');
    setShowDeleteConfirm(true);
  };

  const handleDeleteAll = () => {
    setDeleteType('all');
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (confirmText !== 'تأكيد') {
      toast.error('يرجى كتابة "تأكيد" للمتابعة');
      return;
    }

    setLoading(true);
    try {
      if (deleteType === 'all') {
        await api.delete(`/data-manager/${collectionName}/all`);
        toast.success(`تم مسح جميع بيانات ${collectionLabel}`);
      } else {
        await api.post(`/data-manager/${collectionName}/delete-many`, { ids: selectedIds });
        toast.success(`تم حذف ${selectedIds.length} عنصر`);
      }
      setSelectedIds([]);
      setShowDeleteConfirm(false);
      setConfirmText('');
      setShowManager(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('فشل الحذف: ' + (error.response?.data?.detail || 'خطأ غير معروف'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* زر إدارة البيانات */}
      <Button
        onClick={() => setShowManager(true)}
        variant="outline"
        size="sm"
        className="gap-2 text-red-500 border-red-500/30 hover:bg-red-500/10"
        data-testid="data-manager-btn"
      >
        <Trash2 className="w-4 h-4" />
        إدارة البيانات
      </Button>

      {/* نافذة إدارة البيانات */}
      {showManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/10 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" />
                إدارة بيانات {collectionLabel}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowManager(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* أزرار الإجراءات */}
            <div className="flex gap-3 mb-4">
              <Button
                onClick={selectAll}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                {selectedIds.length === items.length ? (
                  <CheckSquare className="w-4 h-4" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                {selectedIds.length === items.length ? 'إلغاء تحديد الكل' : 'تحديد الكل'}
              </Button>
              
              <Button
                onClick={handleDeleteSelected}
                disabled={selectedIds.length === 0}
                variant="outline"
                size="sm"
                className="gap-2 text-orange-500 border-orange-500/30 hover:bg-orange-500/10"
              >
                <Trash2 className="w-4 h-4" />
                حذف المحدد ({selectedIds.length})
              </Button>

              <Button
                onClick={handleDeleteAll}
                variant="outline"
                size="sm"
                className="gap-2 text-red-500 border-red-500/30 hover:bg-red-500/10"
              >
                <AlertTriangle className="w-4 h-4" />
                مسح الكل
              </Button>
            </div>

            {/* قائمة العناصر */}
            <div className="flex-1 overflow-y-auto border border-white/10 rounded-lg">
              {items.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  لا توجد بيانات
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {items.map((item) => (
                    <div 
                      key={item[idField]}
                      className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-secondary/20 transition-colors ${
                        selectedIds.includes(item[idField]) ? 'bg-primary/10' : ''
                      }`}
                      onClick={() => toggleSelect(item[idField])}
                    >
                      {selectedIds.includes(item[idField]) ? (
                        <CheckSquare className="w-5 h-5 text-primary" />
                      ) : (
                        <Square className="w-5 h-5 text-muted-foreground" />
                      )}
                      <span className="flex-1">
                        {item[nameField] || item.description || item.item_name || item.customer_name || item[idField]}
                      </span>
                      {item.amount && (
                        <span className="text-primary font-bold"><CurrencyDisplay amount={item.amount} className="text-primary" /></span>
                      )}
                      {item.date && (
                        <span className="text-muted-foreground text-sm">{item.date?.split('T')[0]}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 text-sm text-muted-foreground">
              إجمالي العناصر: {items.length} | المحدد: {selectedIds.length}
            </div>
          </div>
        </div>
      )}

      {/* نافذة تأكيد الحذف */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-card border border-red-500/30 rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-500/20 rounded-full">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-red-500">تحذير!</h3>
                <p className="text-sm text-muted-foreground">
                  {deleteType === 'all' 
                    ? `سيتم حذف جميع بيانات ${collectionLabel} (${items.length} عنصر)`
                    : `سيتم حذف ${selectedIds.length} عنصر محدد`
                  }
                </p>
              </div>
            </div>

            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-400 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                هذا الإجراء لا يمكن التراجع عنه!
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                اكتب "تأكيد" للمتابعة:
              </label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="تأكيد"
                className="bg-secondary/50"
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={confirmDelete}
                disabled={loading || confirmText !== 'تأكيد'}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                {loading ? 'جاري الحذف...' : 'حذف نهائي'}
              </Button>
              <Button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setConfirmText('');
                }}
                variant="outline"
                className="flex-1"
              >
                إلغاء
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DataManager;
